import { useState, useRef } from "react";
import { t } from "i18next";
import { ClipMoreMenu } from "@components/ClipMoreMenu/ClipMoreMenu";
import { Checkbox } from "@kalodata/kds-design";
import { IconLineTrash } from "@kalodata/kds-design-icons";
import IconVideo from "@components/Image/icon-video.svg?react";
import { useAppDispatch } from "@hooks/redux";
import { showVideoDisplay } from "@store/globalVideoSlice";
import { useHover } from "ahooks";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import type { UserAssetV2Record } from "@apis/http/api";

interface UIUploadedVideoCardProps {
  record: UserAssetV2Record;
  onDelete?: (record: UserAssetV2Record) => void;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
}

type MediaComposition = "portrait" | "landscapeOrSquare";

const getComposition = (w: number, h: number): MediaComposition => (h > w ? "portrait" : "landscapeOrSquare");

export const UIUploadedVideoCard = ({
  record,
  onDelete,
  selected = false,
  onSelectedChange,
}: UIUploadedVideoCardProps) => {
  const dispatch = useAppDispatch();
  const cardRef = useRef<HTMLDivElement>(null);
  const isHovered = useHover(cardRef);
  const showCheckbox = selected || isHovered;
  const posterUrl = record.vidExtra?.coverImageUrl;
  const [composition, setComposition] = useState<MediaComposition>("portrait");

  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const { videoWidth, videoHeight } = e.currentTarget;
    setComposition(getComposition(videoWidth, videoHeight));
  };

  const handleClick = () => {
    if (!record.url) return;
    dispatch(
      showVideoDisplay({
        type: "video",
        videoURL: record.url,
        aspectRatio: composition === "portrait" ? "RATIO_9_16" : "RATIO_16_9",
        videoId: String(record.id),
      }),
    );
  };

  return (
    <div ref={cardRef} className="relative">
      <ClipMoreMenu
        className="relative w-full overflow-hidden rounded-[8px] bg-kds-dark"
        options={[
          {
            key: "delete",
            label: t("Delete"),
            icon: <IconLineTrash className="text-kds-fg" />,
            danger: true,
            onClick: () => onDelete?.(record),
          },
        ]}
      >
        <div
          className="relative flex w-full aspect-3/4 items-center justify-center overflow-hidden cursor-pointer"
          onClick={handleClick}
        >
          {record.url && (
            <video
              className={classNames(
                "pointer-events-none h-full w-full",
                composition === "portrait" ? "object-cover" : "object-contain",
              )}
              src={record.url}
              poster={posterUrl}
              muted
              playsInline
              preload="metadata"
              onLoadedMetadata={handleLoadedMetadata}
            />
          )}
          <IconVideo className="absolute size-[64px] z-1" />
        </div>
      </ClipMoreMenu>
      <Checkbox
        checked={selected}
        onCheckedChange={(checked) => onSelectedChange?.(checked === true)}
        className={twMerge(
          "absolute left-[12px] top-[12px] z-[10]",
          "size-4 shrink-0 rounded-[4px] shadow-sm",
          "border-0 bg-kds-mask",
          "data-[state=checked]:border-kds-accent data-[state=checked]:bg-kds-accent data-[state=checked]:text-kds-light",
          "data-[state=checked]:**:data-[slot=checkbox-indicator]:grid!",
          "data-[state=checked]:[&_[data-slot=checkbox-indicator]_svg]:size-3",
          "cursor-pointer disabled:cursor-not-allowed disabled:opacity-40",
          showCheckbox ? "inline-flex" : "hidden",
        )}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      />
    </div>
  );
};
